import React, { useEffect, useId } from "react";
import {
	ThemeWrapper,
	PROCTORING_OPTION_TEXT,
	ProctoringOptionCategory,
	VerityInstituteSettings,
	getEditQuizStepName,
} from "@verity/components";
import { CustomSettingData } from "../../settings-page";
import i18n from "../../../../i18n";
import apiClient from "../../../../helpers/api-client";
import { ProctoringOption } from "@verity/proctoring-option/types";
import {
	DEFAULT_PROCTORING_OPTION,
	getProctoringOptionDisabledDetails,
	PROCTORING_OPTION_INFO,
} from "@verity/proctoring-option";
import WarningIcon from "../../../../images/warning.svg?react";
import styles from "./index.module.css";
import { CoreRadioCardIcon, CoreSwitch } from "@verity/components/core";

type ProctoringOptionRowProps = {
	option: keyof ProctoringOption;
	checked: boolean;
	disabled?: boolean;
	onChange: (checked: boolean) => void;
};

const ProctoringOptionRow = ({ option, checked, disabled, onChange }: ProctoringOptionRowProps) => {
	const headerId = useId();

	const Icon = PROCTORING_OPTION_TEXT[option].icon ?? WarningIcon;

	return (
		<div className={styles["proctoring-option-row"]}>
			<div className={styles["proctoring-option-name"]}>
				<CoreRadioCardIcon icon={<Icon aria-hidden="true" />} enabled={checked} />
				<span id={headerId}>{PROCTORING_OPTION_TEXT[option].header}</span>
			</div>
			<div className={styles["proctoring-option-description"]}>{PROCTORING_OPTION_TEXT[option].settingDescription}</div>
			<div className={styles["proctoring-option-switch-wrapper"]}>
				<CoreSwitch
					checked={checked}
					onChange={(e) => onChange(e.currentTarget.checked)}
					disabled={disabled}
					labelTextType="left"
					inputProps={{ "aria-labelledby": headerId }}
				/>
			</div>
		</div>
	);
};

export const InstituteDisabledProctoringOptionsInput = (props: {
	loadData: { verityInstituteSettings: VerityInstituteSettings; instituteDisabledProctoringOptions: string[] };
	setting: CustomSettingData<{ instituteDisabledProctoringOptions: Set<keyof ProctoringOption> }>;
	onSettingChange: (value?: { instituteDisabledProctoringOptions: Set<keyof ProctoringOption> }) => void;
	disabled?: boolean;
}) => {
	const proctoringOptionKeys = new Set(Object.keys(DEFAULT_PROCTORING_OPTION));
	const isProctoringOptionKey = (x: string): x is keyof ProctoringOption => proctoringOptionKeys.has(x);

	const [disabledProctoringOptions, setDisabledProctoringOptions] = React.useState(
		props.setting.value?.instituteDisabledProctoringOptions ??
			new Set(props.loadData.instituteDisabledProctoringOptions.filter(isProctoringOptionKey)),
	);

	useEffect(() => {
		props.onSettingChange({ instituteDisabledProctoringOptions: disabledProctoringOptions });
	}, [disabledProctoringOptions, props]);

	const categories = Object.entries(PROCTORING_OPTION_TEXT).reduce(
		(prev, [k, v]) => {
			const key = k as keyof ProctoringOption;
			if (!v.category || !v.header || !v.settingDescription) return prev;

			const optionInfo = PROCTORING_OPTION_INFO[key];
			const onValues: ProctoringOption[typeof key][] = optionInfo?.isList ? [[""]] : (optionInfo?.onValues ?? []);
			if (
				onValues.every((onValue) => {
					const disabledDetails = getProctoringOptionDisabledDetails(
						key,
						onValue,
						undefined,
						props.loadData.verityInstituteSettings,
					);
					return Object.keys(disabledDetails.disabledReasons).some(
						(reason) => reason === "liteMode" || reason === "methodNotAllowed" || reason === "platformNotAllowed",
					);
				})
			) {
				return prev;
			}

			if (!prev[v.category]) {
				prev[v.category] = [];
			}
			prev[v.category].push(key);
			return prev;
		},
		{} as Record<ProctoringOptionCategory, Array<keyof ProctoringOption>>,
	);

	return (
		<ThemeWrapper>
			<div className={styles["setting-wrapper"]}>
				{Object.keys(categories).map((c, i) => {
					const category = c as ProctoringOptionCategory;
					return (
						<div key={i} style={{ marginTop: i > 0 ? "14px" : 0 }}>
							<div className={styles["proctoring-option-category-label"]}>{getEditQuizStepName(category)}</div>
							<div className={styles["divider"]} />
							<div>
								{categories[category]?.map((option, j) => (
									<ProctoringOptionRow
										key={j}
										option={option}
										checked={!disabledProctoringOptions.has(option)}
										disabled={props.disabled}
										onChange={(checked) =>
											setDisabledProctoringOptions((x) => {
												checked ? x.delete(option) : x.add(option);
												return new Set(x);
											})
										}
									/>
								))}
							</div>
						</div>
					);
				})}
			</div>
		</ThemeWrapper>
	);
};

export async function saveInstituteDisabledProctoringOptions(data?: {
	instituteDisabledProctoringOptions: Set<keyof ProctoringOption>;
}) {
	try {
		if (data?.instituteDisabledProctoringOptions) {
			await apiClient.institute.saveInstituteDisabledProctoringOptions(
				Array.from(data.instituteDisabledProctoringOptions),
			);
		}
		return { ok: true };
	} catch (error) {
		console.error(error);
		return { ok: false, reason: i18n.t("portal.components.settings.instituteGlobalProfile.indexTSX.notSave") };
	}
}
